import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '@app/core/services/auth.service';
import { BlockModalService } from '@app/core/services/block-modal.service';

interface MaintenanceErrorBody {
  success: boolean;
  error: { code: string; message: string };
  maintenanceMode?: boolean;
  scheduledDate?: string;
  scheduledTime?: string;
}

/**
 * CB — Client Blocking (2026-06-30, AD-049 / CB-LD-17). Structured error
 * envelope returned by the backend when a request hits a block gate.
 *
 * Envelope shape (set by the backend's global `ApiExceptionFilter`):
 * ```
 * { success: false,
 *   error: { code, message, blockReason? },
 *   timestamp }
 * ```
 *
 * `blockReason` is present on the 403 mid-session bodies; the 401
 * login-time `ACCOUNT_BLOCKED` body carries the reason inline inside
 * `message` (the backend does not split it into a separate field on
 * 401s — see CB-Client spec §4.3).
 */
interface BlockErrorBody {
  success: boolean;
  error: { code: string; message: string; blockReason?: string };
}

/**
 * Public auth endpoints — legitimately return 401 on bad credentials.
 * Hitting `clearSession()` on these would clobber any in-progress login
 * UX (e.g. the 2FA modal returning to login after a wrong OTP).
 */
const PUBLIC_AUTH_ENDPOINTS: readonly string[] = [
  '/auth/login',
  '/auth/verify-2fa',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/setup-password',
  // 2026-06-08: /auth/me is the bootstrap session-restore call. A 401 on
  // it means "no session yet" (e.g. fresh tab landing on a public page),
  // NOT "session was invalidated — kick the user". The constructor in
  // AuthService also skips the call entirely on public auth paths; this
  // entry is the belt-and-suspenders defence for the race where a
  // session expires mid-restore on a protected route. AuthGuard remains
  // responsible for redirecting unauthenticated users on protected
  // routes, so a silent 401 here is safe.
  '/auth/me',
  // CW-13h LD-8 — the onboarding submit endpoints emit 401
  // ONBOARDING_TOKEN_MISSING/INVALID/EXPIRED. These must be handled
  // locally by CrossPlatformOnboardingService (redirect + clear state)
  // rather than the global clearSession() bounce.
  '/auth/cross-platform/onboarding/personal',
  '/auth/cross-platform/onboarding/corporate',
];

function isPublicAuthEndpoint(url: string): boolean {
  return PUBLIC_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/**
 * Global error interceptor (CW-13d-client §3.3 + CB — Client Blocking).
 *
 * Branch order:
 *   1. 401 ACCOUNT_BLOCKED on a non-public endpoint (mid-session block)
 *      → clearSession + navigate to /auth/login with router state
 *        `{ accountBlocked: true, blockReason }`. LoginComponent reads
 *        the state on init and opens its local BlockedConsentModal.
 *   2. 401 generic on a non-public endpoint → existing clearSession behaviour.
 *   3. 403 ACCOUNT_PARTIALLY_BLOCKED → open partial consent modal.
 *   4. 403 ACCOUNT_TRANSFER_WITHDRAW_BLOCKED → open TW consent modal.
 *   5. 503 with maintenanceMode → navigate to /maintenance.
 *   6. Everything else → pass through.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const blockModalService = inject(BlockModalService);

  return next(req).pipe(
    catchError(
      (error: {
        status: number;
        error?: MaintenanceErrorBody & BlockErrorBody;
        message: string;
      }) => {
        const errorCode = error.error?.error?.code;

        // 1. CB — mid-session ACCOUNT_BLOCKED. The pre-existing token
        //    is still valid but the user has been blocked server-side.
        //    Clear in-memory session and bounce to /auth/login with
        //    router state — LoginComponent will surface the consent modal.
        if (
          error.status === 401 &&
          errorCode === 'ACCOUNT_BLOCKED' &&
          !isPublicAuthEndpoint(req.url)
        ) {
          const blockReason =
            error.error?.error?.blockReason ?? error.error?.error?.message ?? null;
          authService.clearSession();
          router.navigateByUrl('/auth/login', {
            state: { accountBlocked: true, blockReason },
          });
          return throwError(() => error);
        }

        // 2. Generic 401 on a non-public endpoint — existing behaviour.
        // CR-M-1 / SA-CR-M-1 (Prompt 5): explicit early-return mirrors the
        // pattern of branches 1, 3, and 4. Without it, a future branch
        // inserted between 2 and 3 could silently execute additional
        // logic after `clearSession()`. Returning here makes branches
        // mutually exclusive at compile-read time, matching spec §10.2.
        if (
          error.status === 401 &&
          !isPublicAuthEndpoint(req.url) &&
          errorCode !== 'ACCOUNT_BLOCKED'
        ) {
          // Server-side refresh already failed — drop the session.
          authService.clearSession();
          return throwError(() => error);
        }

        // 3. CB — partially-blocked defensive 403. Backend `BlockStatusGuard`
        //    enforces this for users who bypass the client guard via devtools.
        if (error.status === 403 && errorCode === 'ACCOUNT_PARTIALLY_BLOCKED') {
          const reason = error.error?.error?.blockReason ?? null;
          blockModalService.open('partially_blocked', reason);
          return throwError(() => error);
        }

        // 4. CB — transfer/withdraw-blocked defensive 403.
        if (
          error.status === 403 &&
          errorCode === 'ACCOUNT_TRANSFER_WITHDRAW_BLOCKED'
        ) {
          const reason = error.error?.error?.blockReason ?? null;
          blockModalService.open('transfer_withdraw_blocked', reason);
          return throwError(() => error);
        }

        // 403 errors not matching a block code are NOT redirected globally —
        // each page handles its own access errors via catchError. The frontend
        // roleGuard is the proper place for route-level access control.

        if (error.status === 503 && error.error?.maintenanceMode) {
          router.navigate(['/maintenance'], {
            state: {
              message:
                error.error.error?.message ?? 'System is under maintenance. Please try again later.',
              scheduledDate: error.error.scheduledDate,
              scheduledTime: error.error.scheduledTime,
            },
          });
        }

        return throwError(() => error);
      },
    ),
  );
};
